import {
  getProfileInsights,
  upsertProfileInsights,
  pauseActiveObjectives,
  addObjective,
  addGoal,
  addTask
  
} from './journeyService';
import type {UserProfileInsights} from './journeyService';

type Intensity = 'light' | 'moderate' | 'all-in';

function getDefaultTargetDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 4);
  return d.toISOString().slice(0, 10);
}

function inferTargetRole(insights: UserProfileInsights | null): string {
  if (insights?.target_role) return insights.target_role;
  if (insights?.industry) return `${insights.industry} role`;
  if (insights?.technical_strengths?.length) return `${insights.technical_strengths[0]} role`;
  return 'your target role';
}

export interface SetJourneyInput {
  target_role?: string | null;
  target_date?: string | null;
  job_hunt_intensity?: Intensity | null;
}

export async function generateJourney(userInput?: SetJourneyInput): Promise<void> {
  await pauseActiveObjectives();
  let insights = await getProfileInsights();

  if (userInput?.target_role !== undefined || userInput?.target_date !== undefined || userInput?.job_hunt_intensity !== undefined) {
    insights = await upsertProfileInsights({
      target_role: userInput.target_role ?? insights?.target_role ?? null,
      target_date: userInput.target_date ?? insights?.target_date ?? null,
      job_hunt_intensity: userInput.job_hunt_intensity ?? insights?.job_hunt_intensity ?? null,
      skills: insights?.skills,
      strengths: insights?.strengths,
      weaknesses: insights?.weaknesses,
      focus_areas: insights?.focus_areas,
      technical_strengths: insights?.technical_strengths,
      experience_level: insights?.experience_level,
      industry: insights?.industry,
      last_parsed_at: insights?.last_parsed_at,
    });
  }

  const intensity: Intensity = insights?.job_hunt_intensity ?? userInput?.job_hunt_intensity ?? 'moderate';
  const targetRole = inferTargetRole(insights ?? null);
  const targetDate = insights?.target_date ?? userInput?.target_date ?? getDefaultTargetDate();

  const objectiveTitle = `Land a ${targetRole} within ${targetDate}.`;
  const objective = await addObjective(objectiveTitle, targetDate);

  const goalTitles = [
    'Get 2–3 role-specific resumes optimized',
    'Apply to quality jobs consistently',
    'Complete interview prep for your top skills',
  ];
  if (insights?.focus_areas?.length) {
    goalTitles.push(`Strengthen ${insights.focus_areas[0]}`);
  }

  const goalIds: string[] = [];
  for (const title of goalTitles.slice(0, 4)) {
    const g = await addGoal(title, objective.id);
    goalIds.push(g.id);
  }

  const today = new Date().toISOString().slice(0, 10);
  const endOfWeek = new Date();
  endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()));
  const weekEnd = endOfWeek.toISOString().slice(0, 10);

  const applyCount = intensity === 'light' ? 2 : intensity === 'moderate' ? 5 : 10;
  await addTask('Optimize 1 resume this week', {
    goalId: goalIds[0],
    dueDate: weekEnd,
    taskType: 'optimize',
  });
  await addTask(`Apply to ${applyCount} jobs this week`, {
    goalId: goalIds[1],
    dueDate: weekEnd,
    taskType: 'apply',
  });
  await addTask('Do 10 min interview prep today', {
    goalId: goalIds[2],
    dueDate: today,
    taskType: 'prep',
  });
  if (insights?.focus_areas?.length) {
    await addTask(`Practice ${insights.focus_areas[0]} once this week`, {
      goalId: goalIds[3],
      dueDate: weekEnd,
      taskType: 'custom',
    });
  }
}
